// ─── WalletIcons: the unlock control's two faces ───
//
// One control, two states, one place in the layout: the right edge of the
// wallet menu. Locked → FINGERPRINT (unlock). Unlocked → PADLOCK (relock).
// Same slot, so the eye learns one position instead of hunting a text row
// that moves as the menu changes shape.
//
// ─── The fingerprint is Lucide's, adapted (v3) ───
// History, so nobody repeats it: v1 was a potrace of a reference PNG —
// exact likeness, but ridge weight was baked into the filled outlines and
// "thinner" meant re-tracing. v2 was constructed geometry — weight became a
// prop, but nested arcs kept reading as a target or a radio glyph no matter
// how the ellipses were tuned; hand-drawing a convincing print was the wrong
// use of anyone's time. v3 is the professionally drawn one: Lucide's
// `fingerprint` (lucide-static v1.34.0, ISC licence — free for commercial
// use, no attribution required; credited here anyway). Broken segmented
// ridges, which is the specific look that was asked for and that v2 lacked.
//
// The glyph is authored in a 24×24 box; ours is 60 × 57.3 and SHARED with
// the padlock so the state swap never changes the footprint. Rather than
// rewrite Lucide's path data, the group below maps their space into ours.
//
// The transform fits Lucide's NOMINAL 24×24 box: scale 57.3/24 = 2.3875,
// translate x by (60 − 57.3)/2 = 1.35 to centre. Lucide draws with about a
// unit of padding inside that box (measured ink is x 1.00..22.90,
// y 1.00..22.98 including stroke), so the glyph sits very slightly inset —
// which is correct here. It was briefly changed to fit the INK edge-to-edge
// on the theory that the visible border in the wallet menu was dead margin
// around an undersized icon. It was not: that border is .lw-icon-side's own,
// and the fix belonged in CSS. Left at nominal so the print keeps the
// breathing room its designer drew it with.
//
// Two consequences. The transform scales stroke width, so `weight` is in
// LUCIDE-NATIVE units — 2 is the icon as designed (≈4.8 units in our box),
// 1.5 light, 2.5 bold. And fill="none" is load-bearing: these are open
// stroked paths, and SVG's default black fill renders each as a filled blob.
const VIEW_BOX = '0 0 60 57.3'
const FILL = { width: '100%', height: 'auto', display: 'block' }

// `style` merges OVER the fill defaults so a caller can size by height
// instead of width — the header button needs that, since its box is defined by
// the label it replaces, not by the glyph.
export function FingerprintIcon({ className = '', style, weight = 2 }) {
  return (
    <svg viewBox={VIEW_BOX} className={className} style={{ ...FILL, ...style }}
      fill="none" stroke="currentColor" strokeWidth={weight}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <g transform="translate(1.35 0) scale(2.3875)">
        <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
        <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
        <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
        <path d="M2 12a10 10 0 0 1 18-6" />
        <path d="M2 16h.01" />
        <path d="M21.8 16c.2-2 .131-5.354 0-6" />
        <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
        <path d="M8.65 22c.21-.66.45-1.32.57-2" />
        <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
      </g>
    </svg>
  )
}

// SOLID padlock — filled body with the shackle stroked on top and the keyhole
// punched out via fill-rule="evenodd". Matches the flat reference: a shape,
// not a line drawing. Square corners, narrow body, shackle almost as wide as
// the case. Deliberately heavier than the fingerprint, which is all thin
// ridges: the two states are then distinguishable by WEIGHT alone, before
// you've resolved either glyph.
//
// Drawn in the fingerprint's viewBox (see above) so swapping states never
// changes the footprint — the tile doesn't twitch when you unlock.
//
// fill="currentColor" is set HERE and not inherited — the fingerprint sets
// fill="none" for its strokes, so the two components deliberately no longer
// share paint attributes.
//
// The keyhole is ONE subpath, not a circle plus a stem. Under evenodd,
// overlapping subpaths cancel — two shapes would fill the overlap back in and
// leave a bright wedge exactly where the hole should be.
export function PadlockIcon({ className = '', style }) {
  return (
    <svg viewBox={VIEW_BOX} className={className} style={{ ...FILL, ...style }}
      fill="currentColor" aria-hidden="true">
      <path d="M22 26V20.5a8 8 0 0 1 16 0V26" fill="none"
        stroke="currentColor" strokeWidth="6" />
      <path fillRule="evenodd" d="M16 26H44V50H16ZM31.7 38.9L32.9 45.4H27.1L28.3 38.9A3.3 3.3 0 1 1 31.7 38.9Z" />
    </svg>
  )
}
// (addwallet-20261027) SOLID plus, same viewBox/footprint as the padlock so the
// two minis sit as a matched pair at the head of the address row.
export function PlusIcon({ className = '', style }) {
  return (
    <svg viewBox={VIEW_BOX} className={className} style={{ ...FILL, ...style }}
      fill="currentColor" aria-hidden="true">
      <path d="M27 17.25H33V28.25H44V34.25H33V45.25H27V34.25H16V28.25H27Z" />
    </svg>
  )
}
